/**
 * frequently-bought-together.js
 *
 * PDP upsell block. One <section data-fbt> contains N <li data-fbt-card>
 * cards. Each card carries its own OTP/S&S radio group and an Add button.
 *
 * Responsibilities
 * ----------------
 * 1. Radio sync — when a customer picks OTP or S&S inside a card we mark the
 *    label active (the CSS targets `:has(input:checked)` only as a fallback;
 *    we set state explicitly so bfcache restores don't strand the UI).
 *
 * 2. Add to cart — clicking the card's Add button calls
 *    window.WillowCart.add({ id, quantity: 1, selling_plan? }) which opens
 *    the drawer and re-renders it from the Section Rendering API. We fall
 *    back to a direct /cart/add.js + /cart redirect if the drawer JS hasn't
 *    finished loading.
 *
 * 3. Max-qty guard — mirrors the PDP and cart-drawer guard so customers
 *    can't bypass the per-line limit through the FBT card. We read the
 *    canonical /cart.js, sum any same-variant + same-plan lines, and:
 *      - disable the button + show "Max in cart" when the cap is reached
 *      - on click, re-check the server before issuing the add (defends
 *        against stale state from other tabs)
 *    The cap is read from `data-fbt-max-qty` on the section root, which is
 *    sourced from the cart drawer's `section.settings.max_qty_per_item`.
 */

(function () {
  'use strict';

  const root = document.querySelector('[data-fbt]');
  if (!root) return;

  // Read a bundle's component list (rendered server-side by
  // snippets/bundle-components-payload.liquid as a JSON script tag) and
  // return a compact JSON string suitable for the `_bundle_components`
  // line-item property. The checkout sandbox is fresh — no map cache — so
  // the components have to travel inline on the line.
  function readBundleComponentsJson(bundleId) {
    if (!bundleId) return '';
    const scriptEl = document.querySelector(
      'script[data-bundle-components-register][data-bundle-id="' + bundleId + '"]'
    );
    if (!scriptEl) return '';
    try {
      const data = JSON.parse(scriptEl.textContent || '{}');
      const comps = data && data.bundles && data.bundles[bundleId];
      return Array.isArray(comps) && comps.length ? JSON.stringify(comps) : '';
    } catch (e) {
      return '';
    }
  }

  // The cart drawer is the source of truth for the per-line cap. Read it
  // directly if rendered; otherwise fall back to the local data attribute.
  function getMaxQty() {
    const drawer = document.querySelector('cart-drawer');
    const fromDrawer = drawer && Number(drawer.dataset.maxQty);
    if (fromDrawer && fromDrawer > 0) return fromDrawer;
    const fromRoot = Number(root.dataset.fbtMaxQty);
    return fromRoot > 0 ? fromRoot : 4;
  }

  function cardSelectedMode(card) {
    const checked = card.querySelector('.fbt__option-input:checked');
    if (!checked) return 'otp';
    return checked.value === 'subscribe' ? 'subscribe' : 'otp';
  }

  function cardSellingPlanId(card) {
    if (cardSelectedMode(card) !== 'subscribe') return null;
    const id = Number(card.dataset.sellingPlan);
    return id > 0 ? id : null;
  }

  async function lineQtyInCart(variantId, planId) {
    try {
      const res = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) return 0;
      const cart = await res.json();
      return (cart.items || []).reduce(function (sum, line) {
        if (Number(line.variant_id) !== variantId) return sum;
        const linePlan = line.selling_plan_allocation
          ? Number(line.selling_plan_allocation.selling_plan.id)
          : null;
        return linePlan === planId ? sum + line.quantity : sum;
      }, 0);
    } catch (e) {
      return 0;
    }
  }

  // ── Per-card binding ────────────────────────────────────────────────────
  const cards = root.querySelectorAll('[data-fbt-card]');
  cards.forEach(function (card) {
    const variantId = Number(card.dataset.variantId);
    if (!variantId) return;

    const btn      = card.querySelector('[data-fbt-add]');
    const btnText  = btn && btn.querySelector('.fbt__add-text');
    const errorEl  = card.querySelector('[data-fbt-error]');
    const radios   = card.querySelectorAll('.fbt__option-input');
    const defaultText = btn ? (btn.dataset.defaultText || btn.textContent.trim()) : '';
    const loadingText = btn ? (btn.dataset.loadingText || 'Adding...') : '';
    const maxText  = btn ? (btn.dataset.maxText || 'Max in cart') : '';

    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    async function refreshButtonAvailability() {
      if (!btn) return;
      const planId = cardSellingPlanId(card);
      const inCart = await lineQtyInCart(variantId, planId);
      const atMax  = inCart >= getMaxQty();
      btn.disabled = atMax;
      if (atMax) {
        btn.dataset.state = 'max';
        if (btnText) btnText.textContent = maxText;
        btn.setAttribute('aria-label', maxText);
      } else {
        delete btn.dataset.state;
        if (btnText) btnText.textContent = defaultText;
        btn.setAttribute('aria-label', defaultText);
      }
    }

    // Radios — clear error + recompute availability when the mode changes,
    // since "in cart" for OTP and "in cart" for S&S are independent lines.
    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        clearError();
        refreshButtonAvailability();
      });
    });

    // Add button
    if (btn) {
      btn.addEventListener('click', async function (event) {
        event.preventDefault();
        if (btn.disabled) return;
        clearError();
        // Mirror the main product form's "Adding..." loading state: swap the
        // label, disable the button so a hot-key repeat can't double-add,
        // and let CSS hide the "+" icon via [data-state='loading'].
        btn.dataset.state = 'loading';
        btn.disabled = true;
        if (btnText) btnText.textContent = loadingText;
        btn.setAttribute('aria-label', loadingText);
        const planId = cardSellingPlanId(card);

        try {
          // Re-check the canonical cart RIGHT before adding to defend against
          // stale state (other tabs, slow refresh after a previous mutation,
          // etc.). The /cart.js read is cheap and avoids a UX where the
          // customer can click past the cap.
          const inCart = await lineQtyInCart(variantId, planId);
          if (inCart >= getMaxQty()) {
            await refreshButtonAvailability();
            return;
          }

          const payload = { id: variantId, quantity: 1 };
          if (planId) payload.selling_plan = planId;

          // Bundle marker so the analytics pixel can detect bundles via the
          // cart snapshot's line-item properties (mirrors the PDP form).
          // _bundle_components inlines the component list so the checkout
          // sandbox can read it directly — see product.js for the rationale.
          if (card.dataset.isBundle === 'true') {
            const bundleId = card.dataset.productId || '';
            const props = {
              _item_type:   'bundle',
              _bundle_id:   bundleId,
              _bundle_name: card.dataset.bundleName || '',
            };
            const componentsJson = readBundleComponentsJson(bundleId);
            if (componentsJson) props._bundle_components = componentsJson;
            payload.properties = props;
          }

          if (window.WillowCart && typeof window.WillowCart.add === 'function') {
            await window.WillowCart.add(payload);
          } else {
            // Fallback path — drawer hasn't loaded yet. Submit a vanilla
            // /cart/add.js and redirect to /cart so the customer still gets
            // their item in the cart.
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify(payload),
            });
            if (!res.ok) {
              const data = await res.json().catch(function () { return {}; });
              throw new Error(data.description || data.message || 'Could not add to cart.');
            }
            window.location = '/cart';
            return;
          }
        } catch (err) {
          showError(err && err.message ? err.message : 'Could not add to cart.');
        } finally {
          if (btn.dataset.state === 'loading') delete btn.dataset.state;
          btn.disabled = false;
          if (btnText) btnText.textContent = defaultText;
          btn.setAttribute('aria-label', defaultText);
          // refreshButtonAvailability will re-disable + swap to "max" copy
          // if we actually hit the cap with this addition.
          await refreshButtonAvailability();
        }
      });
    }

    // Initial pass and refresh after every cart mutation.
    refreshButtonAvailability();
    document.addEventListener('cart:updated', refreshButtonAvailability);
    window.addEventListener('pageshow', refreshButtonAvailability);
  });
})();
